import { Event, EVENTS_FILTER_KEYS, GetEventsQueryFilters, useGetEventsQuery } from '@schooly/api';
import { FC, useCallback, useMemo, useState } from 'react';

import { DropdownSelectProps } from '../../DropdownSelect';
import { pickOnlyParamsFromFilterKeys } from '../../utils';
import { EventRecurrenceLabelSelect } from '../RecurrenceLabelSelect/EventRecurrenceSelectLabel';
import { getRecurrences } from '../utils';
import { RecurrenceSelect } from './RecurrenceSelect';

type EventRecurrenceSelectProps = {
  schoolId: string;
  selectedId?: string;
  onSelectRecurrence: (v: string) => void;
  filters?: GetEventsQueryFilters;
} & Omit<DropdownSelectProps, 'children' | 'renderContent'>;
export const EventRecurrenceSelect: FC<EventRecurrenceSelectProps> = ({
  schoolId,
  filters = {},
  selectedId,
  onSelectRecurrence,
  onClear,
  onClose,
  ...dropdownProps
}) => {
  const [queryEnabled, setQueryEnabled] = useState(false);

  const { data, hasNextPage, params, setParams, isLoading, isFetchingNextPage, fetchNextPage } =
    useGetEventsQuery(
      {
        schoolId,
        query: '',
        filters: pickOnlyParamsFromFilterKeys(EVENTS_FILTER_KEYS, filters),
      },
      {
        enabled: queryEnabled,
      },
    );

  const handleChangeQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  const events = useMemo(
    () => data?.pages.reduce<Event[]>((prev, curr) => [...prev, ...curr.results], []) ?? [],
    [data?.pages],
  );
  const recurrences = getRecurrences(events);

  const renderLabel = useCallback(
    (title: string) => {
      return <EventRecurrenceLabelSelect title={title} id={selectedId ?? ''} schoolId={schoolId} />;
    },
    [schoolId, selectedId],
  );

  return (
    <RecurrenceSelect
      {...dropdownProps}
      onToggle={setQueryEnabled}
      schoolId={schoolId}
      selectedId={selectedId}
      onSelectRecurrence={onSelectRecurrence}
      onClear={onClear}
      onClose={onClose}
      recurrences={recurrences}
      isLoading={isLoading || isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      handleChangeQuery={handleChangeQuery}
      hasNextPage={hasNextPage}
      query={params.query}
      renderLabel={renderLabel}
    />
  );
};
